import React from 'react';
import PropTypes from 'prop-types';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { Icon } from 'utils/icons';

const reorderColumnKeys = (columns, sourceIndex, destinationIndex) => {
  if (sourceIndex === destinationIndex)
    return columns.map(column => column.key);

  const nextColumns = [...columns];
  const [movedColumn] = nextColumns.splice(sourceIndex, 1);

  if (!movedColumn) return columns.map(column => column.key);

  nextColumns.splice(destinationIndex, 0, movedColumn);
  return nextColumns.map(column => column.key);
};

const DragHandle = props => (
  <span
    className="detail-column-manager__drag-handle"
    aria-label="Move column"
    {...props}
  >
    <span />
    <span />
    <span />
    <span />
    <span />
    <span />
  </span>
);

const ColumnToggle = ({ checked, disabled, label, onChange }) => (
  <button
    type="button"
    className={[
      'detail-column-manager__toggle',
      checked ? 'is-on' : 'is-off',
    ].join(' ')}
    aria-label={`${checked ? 'Hide' : 'Show'} ${label} column`}
    aria-pressed={checked}
    disabled={disabled}
    onClick={onChange}
  >
    <span />
  </button>
);

ColumnToggle.propTypes = {
  checked: PropTypes.bool.isRequired,
  disabled: PropTypes.bool,
  label: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
};

ColumnToggle.defaultProps = {
  disabled: false,
};

const ColumnManager = ({
  columns,
  onClose,
  onDone,
  onReset,
  onToggleColumn,
  onReorderColumns,
  onReorderColumnsByKey,
  onApplyColumnOrder,
  onPreviewColumnOrder,
  onClearColumnPreview,
  style,
}) => {
  const handleDragUpdate = result => {
    if (!result.destination) {
      onClearColumnPreview?.();
      return;
    }

    const nextOrder = reorderColumnKeys(
      columns,
      result.source.index,
      result.destination.index
    );

    onPreviewColumnOrder?.(nextOrder);
  };

  const handleDragEnd = result => {
    if (!result.destination) {
      onClearColumnPreview?.();
      return;
    }

    const nextOrder = reorderColumnKeys(
      columns,
      result.source.index,
      result.destination.index
    );

    if (onApplyColumnOrder) {
      onApplyColumnOrder(nextOrder);
      onClearColumnPreview?.();
      return;
    }

    const sourceColumn = columns[result.source.index];
    const destinationColumn = columns[result.destination.index];

    if (sourceColumn && destinationColumn && onReorderColumnsByKey) {
      onReorderColumnsByKey(
        sourceColumn.key,
        destinationColumn.key,
        result.destination.index > result.source.index
      );
      onClearColumnPreview?.();
      return;
    }

    onReorderColumns(result.source.index, result.destination.index);
    onClearColumnPreview?.();
  };

  return (
    <aside
      className="detail-column-manager"
      id="detail-column-manager"
      aria-label="Column Manager"
      style={style}
    >
      <div className="detail-column-manager__header">
        <h2>Column Manager</h2>
        <button
          type="button"
          className="detail-column-manager__close"
          aria-label="Close column manager"
          onClick={onClose}
        >
          <Icon name="CloseIcon" />
        </button>
      </div>

      <DragDropContext
        onDragEnd={handleDragEnd}
        onDragUpdate={handleDragUpdate}
      >
        <Droppable droppableId="detail-column-manager-columns">
          {provided => (
            <div
              className="detail-column-manager__list"
              ref={provided.innerRef}
              {...provided.droppableProps}
            >
              {columns.map((column, index) => {
                const isVisible = !column.hidden || column.locked;

                return (
                  <Draggable
                    key={column.key}
                    draggableId={`detail-column-manager-${column.key}`}
                    index={index}
                  >
                    {(dragProvided, snapshot) => (
                      <div
                        ref={dragProvided.innerRef}
                        className={[
                          'detail-column-manager__row',
                          !isVisible ? 'is-hidden' : '',
                          snapshot.isDragging ? 'is-dragging' : '',
                        ]
                          .filter(Boolean)
                          .join(' ')}
                        {...dragProvided.draggableProps}
                      >
                        <div className="detail-column-manager__row-label">
                          <DragHandle {...dragProvided.dragHandleProps} />
                          <span className="detail-column-manager__column-copy">
                            <span className="detail-column-manager__column-name">
                              {column.label}
                            </span>
                            {column.overtimeLabel !== undefined && (
                              <span className="detail-column-manager__overtime-name">
                                {column.overtimeLabel}
                              </span>
                            )}
                          </span>
                        </div>
                        <ColumnToggle
                          checked={isVisible}
                          disabled={column.locked}
                          label={column.label}
                          onChange={() => onToggleColumn(column.key)}
                        />
                      </div>
                    )}
                  </Draggable>
                );
              })}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      <div className="detail-column-manager__footer">
        <button
          type="button"
          className="detail-column-manager__reset"
          onClick={onReset}
        >
          Reset All
        </button>
        <button
          type="button"
          className="detail-column-manager__done"
          onClick={onDone}
        >
          Done
        </button>
      </div>
    </aside>
  );
};

ColumnManager.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      hidden: PropTypes.bool.isRequired,
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      locked: PropTypes.bool,
      overtimeLabel: PropTypes.string,
    })
  ).isRequired,
  onClose: PropTypes.func.isRequired,
  onDone: PropTypes.func.isRequired,
  onApplyColumnOrder: PropTypes.func,
  onClearColumnPreview: PropTypes.func,
  onPreviewColumnOrder: PropTypes.func,
  onReset: PropTypes.func.isRequired,
  onToggleColumn: PropTypes.func.isRequired,
  onReorderColumns: PropTypes.func.isRequired,
  onReorderColumnsByKey: PropTypes.func,
  style: PropTypes.shape({}),
};

ColumnManager.defaultProps = {
  onApplyColumnOrder: null,
  onClearColumnPreview: null,
  onPreviewColumnOrder: null,
  onReorderColumnsByKey: null,
  style: null,
};

export default ColumnManager;
